/**
 * US Postal Code geo utilities.
 *
 * Provides postal-code-to-coordinates lookup and city/state/postal code autocomplete.
 *
 * Data source: static CSV of US postal codes with lat/lng.
 */
import type { GeoCoordinatesDto, PostalCodeResultDto } from './schema.ts';

/** Format a postal code record as a display label. */
export const formatPostalCodeLabel = (r: {
  postalCode: string;
  city: string;
  state: string;
}) => `${r.postalCode} - ${r.city}, ${r.state}`;

/** Record with pre-computed lowercase fields for fast autocomplete matching. */
interface SearchablePostalCodeRecord extends PostalCodeResultDto {
  cityLower: string;
  stateLower: string;
  cityStateLower: string;
}

// --- In-memory postal code store ---

let searchableRows: SearchablePostalCodeRecord[] = [];
let postalCodeMap: Map<string, PostalCodeResultDto> = new Map();

/**
 * Load postal code data from a pre-parsed array.
 * Call this once at startup with parsed CSV data.
 */
export const loadPostalCodeData = (entries: PostalCodeResultDto[]): void => {
  searchableRows = entries.map((e) => ({
    ...e,
    cityLower: e.city.toLowerCase(),
    stateLower: e.state.toLowerCase(),
    cityStateLower: `${e.city}, ${e.state}`.toLowerCase(),
  }));
  postalCodeMap = new Map(entries.map((e) => [e.postalCode, e]));
};

/**
 * Get coordinates for a US postal code.
 * Returns null if the postal code is not found.
 */
export const getCoordsByPostalCode = (
  postalCode: string,
): GeoCoordinatesDto | null => {
  const entry = postalCodeMap.get(postalCode.trim());
  if (!entry) {
    return null;
  }
  return { lat: entry.lat, lng: entry.lng };
};

/**
 * Autocomplete search for city, state, or postal code.
 * Returns up to `limit` matching entries (case-insensitive prefix match).
 */
export const autocompleteLocation = (
  query: string,
  limit = 10,
): PostalCodeResultDto[] => {
  const q = query.trim().toLowerCase();
  if (!q) {
    return [];
  }

  const results: PostalCodeResultDto[] = [];

  for (const entry of searchableRows) {
    if (results.length >= limit) {
      break;
    }

    if (
      entry.postalCode.startsWith(q) ||
      entry.cityLower.startsWith(q) ||
      entry.stateLower.startsWith(q) ||
      entry.cityStateLower.startsWith(q)
    ) {
      results.push(entry);
    }
  }

  return results;
};

/**
 * Whether a latitude/longitude pair is usable for geo queries.
 *
 * Accepts only finite numbers, rejecting `undefined`, `null`, `NaN`, and
 * `Infinity` while treating `0` (equator / prime meridian) as valid.
 */
export const hasValidCoordinates = (
  latitude: unknown,
  longitude: unknown,
): boolean =>
  typeof latitude === 'number' &&
  Number.isFinite(latitude) &&
  typeof longitude === 'number' &&
  Number.isFinite(longitude);
